// El panel de una toma de un producto: el prompt entero editable (editar lo fija), las referencias
// que viajan —las fotos del producto y las imágenes que subió la persona, como miniaturas—, el
// estado, la imagen si salió, rehacer y eliminar la toma de este producto. Va en `HojaLateral`, la
// de Diseño.
import { ChevronDown, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { HojaLateral } from '../../../../chat-atlas/ui/HojaLateral';
import CachedImg from '../../../../components/CachedImg';
import { CreditBadge } from '../../../../components/CreditBadge';
import { creditosPorResolucion } from '../../../../lib/resolucionImagen';
import { preloadGeneratedImages } from '../../../../utils/preloadGeneratedImages';
import { signReferenceImage } from '../../agentAttachments';
import { VisorDeAdjunto } from '../../components/VisorDeAdjunto';
import { fetchProductImageUrls } from '../../productsApi';
import type { ItemDelPlan, PlanDeCatalogo, ProductoDelPlan, TomaDelPlan } from './plan';

export interface PanelDeItemProps {
  plan: PlanDeCatalogo;
  producto: ProductoDelPlan;
  toma: TomaDelPlan;
  item: ItemDelPlan;
  companyId: string | null;
  generando: boolean;
  onEditarPrompt: (prompt: string) => void;
  onDesfijar: () => void;
  onRehacer: () => void;
  onReescribirConElAgente?: () => void;
  /** Eliminar esta toma de este producto (si tiene foto, se pide confirmación). */
  onQuitar?: () => void;
  onCerrar: () => void;
}

export const PanelDeItem: React.FC<PanelDeItemProps> = ({
  plan,
  producto,
  toma,
  item,
  companyId,
  generando,
  onEditarPrompt,
  onDesfijar,
  onRehacer,
  onReescribirConElAgente,
  onQuitar,
  onCerrar,
}) => {
  const [url, setUrl] = useState<string | null>(null);
  const [detalles, setDetalles] = useState(false);
  // Una referencia tocada, en grande (el visor del chat, sólo para mirar).
  const [ampliada, setAmpliada] = useState<{ url: string; nombre: string } | null>(null);
  const primeraImagen = item.imageIds?.[0];
  useEffect(() => {
    setUrl(null);
    if (!primeraImagen || !companyId) return;
    let vivo = true;
    void preloadGeneratedImages([primeraImagen], companyId)
      .then(([img]) => {
        if (vivo && img) setUrl(img.signedUrl);
      })
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, [primeraImagen, companyId]);

  // Las referencias que viajan, como miniaturas: las fotos del producto (de `productos_imagenes`)
  // y las imágenes de la persona que la toma lista (firmadas).
  const [fotosDelProducto, setFotosDelProducto] = useState<string[]>([]);
  const [imagenesDeReferencia, setImagenesDeReferencia] = useState<Array<{ id: string; etiqueta: string; url: string | null }>>([]);
  const referenciasDeLaToma = (toma.referencias ?? []).map((id) => plan.referencias.find((r) => r.id === id)).filter((r): r is NonNullable<typeof r> => !!r);
  const claveDeReferencias = referenciasDeLaToma.map((r) => r.id).join(',');
  useEffect(() => {
    let vivo = true;
    if (companyId) {
      void fetchProductImageUrls(companyId, producto.varianteId)
        .then((urls) => vivo && setFotosDelProducto(urls))
        .catch(() => {});
    }
    void Promise.all(
      referenciasDeLaToma.map(async (r) => ({ id: r.id, etiqueta: r.etiqueta, url: await signReferenceImage(r.storagePath, r.bucket).catch(() => null) })),
    ).then((lista) => vivo && setImagenesDeReferencia(lista));
    return () => {
      vivo = false;
    };
    // `claveDeReferencias` resume la lista: con el array como dependencia correría en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, producto.varianteId, claveDeReferencias]);
  const quitar = () => {
    if (!onQuitar) return;
    const tieneFoto = item.estado === 'generada' && (item.imageIds?.length ?? 0) > 0;
    if (tieneFoto && !window.confirm(`¿Eliminar la toma ${toma.titulo} de ${producto.nombre}? La foto queda en Creaciones.`)) return;
    onQuitar();
  };
  const puedeRehacer = (item.estado === 'generada' || item.estado === 'fallo') && !!item.prompt.trim() && !generando;
  const pie = (
    <div className="flex items-center gap-2">
      {onQuitar && (
        <button
          type="button"
          onClick={quitar}
          title={`Eliminar la toma ${toma.titulo} de ${producto.nombre}`}
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md py-2 px-3 text-[14px] font-semibold text-[#B42318] hover:bg-[#FFF5F4]"
        >
          <Trash2 className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
          Eliminar
        </button>
      )}
      {onReescribirConElAgente && (
        <button
          type="button"
          onClick={onReescribirConElAgente}
          title="Pedirle al asistente que reescriba este prompt"
          className="whitespace-nowrap rounded-md py-2 px-4 text-[14px] font-semibold text-brand-primary hover:bg-[rgb(var(--brand-primary-rgb)_/_0.095)]"
        >
          Reescribir
        </button>
      )}
      <div className="flex flex-1 justify-end gap-2">
        {puedeRehacer && (
          <button
            type="button"
            onClick={onRehacer}
            className="inline-flex items-center gap-2 rounded-md bg-brand-primary py-2 px-4 text-[14px] font-semibold text-white hover:bg-brand-primary-hover"
          >
            <RefreshCw className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
            Rehacer
            <CreditBadge credits={toma.cantidad * creditosPorResolucion(plan.resolucion)} tone="on-primary" size="sm" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
    <HojaLateral
      abierta
      titulo={`${producto.nombre} · ${toma.titulo}`}
      onCerrar={onCerrar}
      pie={pie}
      sinFondo
      // En el teléfono, casi todo el alto: es para leer y editar el prompt entero.
      altoMobile="calc(100dvh - max(env(safe-area-inset-top), 12px))"
    >
      <div className="flex flex-col gap-4 px-1 pb-2">
        {/* Lo que el agente le pidió al escritor para esta toma: es interno, así que va plegado
            debajo del título y no compite con el prompt (ADR-2026-10-15-la-instruccion-de-la-toma-va-en-detalles). */}
        {toma.queMuestra.trim() && (
          <div className="-mt-1">
            <button
              type="button"
              onClick={() => setDetalles((v) => !v)}
              aria-expanded={detalles}
              className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#3C3C43]/60 transition-colors hover:text-brand-black"
            >
              Detalles
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${detalles ? 'rotate-180' : ''}`} strokeWidth={2.25} aria-hidden="true" />
            </button>
            {detalles && <p className="animate-fade-in mt-1.5 text-[12.5px] leading-snug text-[#3C3C43]/55">{toma.queMuestra}</p>}
          </div>
        )}
        {item.estado === 'generando' && (
          <p className="inline-flex items-center gap-2 text-[13.5px] text-brand-primary">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Generando…
          </p>
        )}
        {item.estado === 'fallo' && <p className="rounded-lg bg-[#FFF5F4] px-3 py-2 text-[13.5px] text-[#B42318]">{item.motivo ?? 'No salió.'}</p>}
        {url && <CachedImg src={url} alt={`${producto.nombre}, ${toma.titulo}`} className="w-full rounded-xl object-contain" />}
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <p className="text-[13px] font-semibold text-brand-black">Prompt{item.fijado ? ' · fijado' : ''}</p>
            {item.fijado && (
              <button type="button" onClick={onDesfijar} className="text-[12.5px] font-semibold text-brand-primary hover:underline">
                Dejar que el escritor lo reescriba
              </button>
            )}
          </div>
          <textarea
            value={item.prompt}
            onChange={(e) => onEditarPrompt(e.target.value)}
            rows={22}
            placeholder="Todavía sin prompt: pedile al asistente que escriba, o escribilo acá."
            className="min-h-[48vh] w-full resize-y rounded-lg border border-black/[0.1] px-3 py-2 font-mono text-[12.5px] leading-relaxed text-brand-black"
          />
          <p className="mt-1 text-[12px] text-[#3C3C43]/50">
            {toma.infografia ? 'Es la plantilla de ficha técnica llena con las medidas y la ficha del producto. ' : ''}Editarlo a mano lo fija: el escritor no lo pisa.
          </p>
        </div>
        <div>
          <p className="mb-1.5 text-[13px] font-semibold text-brand-black">Referencias</p>
          <div className="flex flex-wrap gap-2">
            {(fotosDelProducto.length > 0 ? fotosDelProducto : producto.portadaUrl ? [producto.portadaUrl] : []).map((url, i) => (
              <figure key={url} className="flex w-[72px] flex-col gap-1">
                <button
                  type="button"
                  onClick={() => setAmpliada({ url, nombre: i === 0 ? producto.nombre : `${producto.nombre}, vista ${i + 1}` })}
                  aria-label={`Ver en grande: ${i === 0 ? producto.nombre : `vista ${i + 1}`}`}
                  className="rounded-lg transition-transform hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
                >
                  <CachedImg src={url} alt="" className="h-[72px] w-[72px] rounded-lg bg-[#F5F6FA] object-cover ring-1 ring-black/[0.06]" />
                </button>
                <figcaption className="truncate text-[11px] text-[#3C3C43]/60" title={producto.nombre}>
                  {i === 0 ? producto.nombre : `Vista ${i + 1}`}
                </figcaption>
              </figure>
            ))}
            {imagenesDeReferencia.map((r) => (
              <figure key={r.id} className="flex w-[72px] flex-col gap-1">
                {r.url ? (
                  <button
                    type="button"
                    onClick={() => r.url && setAmpliada({ url: r.url, nombre: r.etiqueta })}
                    aria-label={`Ver en grande: ${r.etiqueta}`}
                    className="rounded-lg transition-transform hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
                  >
                    <CachedImg src={r.url} alt="" className="h-[72px] w-[72px] rounded-lg bg-[#F5F6FA] object-cover ring-1 ring-black/[0.06]" />
                  </button>
                ) : (
                  <span className="grid h-[72px] w-[72px] place-items-center rounded-lg bg-[#F5F6FA] text-[11px] text-[#3C3C43]/50 ring-1 ring-black/[0.06]">sin vista</span>
                )}
                <figcaption className="truncate text-[11px] text-[#3C3C43]/60" title={r.etiqueta}>
                  {r.etiqueta}
                </figcaption>
              </figure>
            ))}
          </div>
          {toma.fondoBlanco && <p className="mt-1.5 text-[12.5px] text-[#3C3C43]/60">Fondo blanco: lo pone el sistema, con el punto blanco corregido.</p>}
        </div>
      </div>
    </HojaLateral>
    {/* Afuera de la hoja: HojaLateral dibuja su contenido dos veces (mobile y escritorio). */}
    {ampliada && <VisorDeAdjunto url={ampliada.url} nombre={ampliada.nombre} onCerrar={() => setAmpliada(null)} />}
    </>
  );
};
